import { Component, ChangeDetectionStrategy, ChangeDetectorRef, inject, input, output, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TextareaModule } from 'primeng/textarea';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TabsModule } from 'primeng/tabs';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, tap, catchError, of } from 'rxjs';
import {
  ProductService,
  CreateProductRequest,
  UpdateProductRequest,
  ProductDTO
} from '../../services/product.service';
import { ProductTaxService, ProductTaxDTO } from '../../services/product-tax.service';
import { CurrencyService, CurrencyDTO } from '../../services/currency.service';
import { MeasureUnitService, MeasureUnitDTO } from '../../services/measure-unit.service';
import { TaxProductCodeService, TaxProductCodeDTO } from '../../services/tax-product-code.service';
import { SATObjetoImpService, SATObjetoImpDTO } from '../../services/sat-objeto-imp.service';
import { EntityAuditPanelComponent } from '../shared/entity-audit/entity-audit-panel.component';

function autocompleteOptionIfPresent(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value == null || value === '') {
    return null;
  }
  if (typeof value !== 'object' || (value as { id?: unknown }).id == null) {
    return { incompleteSelection: true };
  }
  return null;
}

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host ::ng-deep .sale-form-card.p-card .p-card-header {
        padding: 0;
      }
      :host ::ng-deep .sale-form-card.p-card .p-card-body {
        padding-top: 0.25rem;
      }
      :host ::ng-deep .sale-form-card .p-tablist,
      :host ::ng-deep .sale-form-card .p-tabview-tablist,
      :host ::ng-deep .sale-form-card .p-tabs-nav,
      :host ::ng-deep .sale-form-card [data-pc-section='tablist'] {
        padding-top: 0;
        margin-top: 0;
      }
      :host ::ng-deep .sale-form-card .p-tabpanels,
      :host ::ng-deep .sale-form-card .p-tabpanel {
        padding-top: 0.5rem;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    TextareaModule,
    FloatLabelModule,
    SelectModule,
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    TabsModule,
    CheckboxModule,
    TooltipModule,
    EntityAuditPanelComponent
  ]
})
export class ProductFormComponent {
  readonly embedded = input(false);
  readonly saved = output<ProductDTO>();
  readonly dismissed = output<void>();

  private fb = inject(FormBuilder);
  private productService = inject(ProductService);
  private currencyService = inject(CurrencyService);
  private measureUnitService = inject(MeasureUnitService);
  private taxProductCodeService = inject(TaxProductCodeService);
  private productTaxService = inject(ProductTaxService);
  private satObjetoImpService = inject(SATObjetoImpService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  private measureUnitSelecting = false;
  private taxCodeSelecting = false;

  productForm: FormGroup;
  loading = signal(false);
  /** Overlay de carga inicial o refresh del producto (no guardado ni otras acciones). */
  formDataLoading = signal(false);
  error = signal<string | null>(null);
  isEditMode = signal(false);
  editingProductId = signal<number | null>(null);
  currentProduct = signal<ProductDTO | null>(null);
  selectedTaxes = signal<ProductTaxDTO[]>([]);
  productTaxOptions = signal<ProductTaxDTO[]>([]);
  productTaxLoading = signal(false);

  allCurrencies = signal<CurrencyDTO[]>([]);
  currencyOptions = signal<CurrencyDTO[]>([]);
  measureUnitOptions = signal<MeasureUnitDTO[]>([]);
  taxCodeOptions = signal<TaxProductCodeDTO[]>([]);
  satObjetoImpOptions = signal<SATObjetoImpDTO[]>([]);
  currencyLoading = signal(false);
  measureUnitLoading = signal(false);
  taxCodeLoading = signal(false);
  

  stateOptions = [
    { value: 'ACTIVE', label: 'Activo' },
    { value: 'INACTIVE', label: 'Inactivo' }
  ];

  taxInclusiveOptions = [
    { value: true, label: 'Sí' },
    { value: false, label: 'No' }
  ];

  taxApplicationOptions = [
    { value: 'TAXABLE', label: 'Gravado' },
    { value: 'EXEMPT', label: 'Exento' },
    { value: 'ZERO_RATED', label: 'Tasa 0%' }
  ];

  taxCollectionTypeOptions = [
    { value: 'SELLER_COLLECTS', label: 'Traslado' },
    { value: 'SELLER_WITHHOLDS', label: 'Retención' }
  ];

  activeTab = signal<string>('general');
  tabs = [
    { value: 'general', label: 'General', icon: 'pi pi-box' },
    { value: 'auditoria', label: 'Auditoría', icon: 'pi pi-history' }
  ] as const;

  get currencySelectOptions(): { id: number; label: string }[] {
    return this.allCurrencies().map(c => ({ id: c.id, label: `${c.code} - ${c.name}` }));
  }

  get currencyCode(): string {
    const id = this.productForm.get('currencyId')?.value ?? this.currentProduct()?.currency?.id;
    const currency = this.allCurrencies().find(c => c.id === id);
    return currency?.code ?? 'MXN';
  }

  get productSubtotal(): number {
    const price = this.currentProduct()?.unitPrice ?? this.productForm.get('unitPrice')?.value;
    return Number(price) || 0;
  }

  get trasladosAmount(): number {
    const subtotal = this.productSubtotal;
    return this.selectedTaxes().reduce((sum, t) => {
      if (t.collectionType !== 'SELLER_COLLECTS') return sum;
      const amount = t.taxAmount != null ? Number(t.taxAmount) : (t.taxRate != null ? subtotal * Number(t.taxRate) / 100 : 0);
      return sum + amount;
    }, 0);
  }

  get retencionesAmount(): number {
    const subtotal = this.productSubtotal;
    return this.selectedTaxes().reduce((sum, t) => {
      if (t.collectionType !== 'SELLER_WITHHOLDS') return sum;
      const amount = t.taxAmount != null ? Number(t.taxAmount) : (t.taxRate != null ? subtotal * Number(t.taxRate) / 100 : 0);
      return sum + amount;
    }, 0);
  }

  get productTotal(): number {
    return this.productSubtotal + this.trasladosAmount - this.retencionesAmount;
  }

  constructor() {
    this.productForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: ['', [Validators.maxLength(1000)]],
      sku: ['', [Validators.maxLength(100)]],
      externalId: ['', [Validators.maxLength(100)]],
      unitPrice: [0, [Validators.required, Validators.min(0)]],
      currencyId: [null, [Validators.required]],
      measureUnitSelection: [null, [autocompleteOptionIfPresent]],
      taxCodeSelection: [null, [autocompleteOptionIfPresent]],
      satObjetoImpId: [null],
      measureUnitId: [''],
      taxCodeId: [''],
      taxInclusive: [null as boolean | null],
      productTaxIds: [[] as number[]],
      state: ['ACTIVE'],
      currencySearch: [''],
      measureUnitSearch: [''],
      taxCodeSearch: ['']
    });

    this.setupSearchSubscriptions();
    this.loadAllCurrencies();
    this.loadAllProductTaxes();
    this.loadAllSatObjetoImps();
  }

  ngOnInit(): void {
    if (this.embedded()) {
      return;
    }

    this.route.paramMap.subscribe(params => {
      const routeId = params.get('id');
      if (routeId) {
        const productId = Number(routeId);
        if (!Number.isNaN(productId)) {
          this.isEditMode.set(true);
          this.editingProductId.set(productId);
          this.updateRelationValidators(true);
          this.loadProduct(productId);
          return;
        }
      }

      this.isEditMode.set(false);
      this.editingProductId.set(null);
      this.currentProduct.set(null);
      this.selectedTaxes.set([]);
      this.updateRelationValidators(false);
      this.productForm.reset({
        name: '',
        description: '',
        sku: '',
        externalId: '',
        unitPrice: 0,
        currencyId: this.getDefaultCurrencyId(),
        measureUnitSelection: null,
        taxCodeSelection: null,
        satObjetoImpId: null,
        measureUnitId: '',
        taxCodeId: '',
        taxInclusive: null,
        productTaxIds: [],
        state: 'ACTIVE',
        currencySearch: '',
        measureUnitSearch: '',
        taxCodeSearch: ''
      });
    });
  }

  isProductTaxSelected(id: number): boolean {
    const ids: number[] = this.productForm.get('productTaxIds')?.value ?? [];
    return ids.includes(id);
  }

  toggleProductTax(id: number, checked: boolean): void {
    const current: number[] = [...(this.productForm.get('productTaxIds')?.value ?? [])];
    const next = checked
      ? current.includes(id) ? current : [...current, id]
      : current.filter(taxId => taxId !== id);
    this.productForm.patchValue({ productTaxIds: next });
    this.syncSelectedTaxesFromForm();
    this.cdr.markForCheck();
  }

  private syncSelectedTaxesFromForm(): void {
    const ids: number[] = this.productForm.get('productTaxIds')?.value ?? [];
    this.selectedTaxes.set(this.productTaxOptions().filter(t => ids.includes(t.id)));
  }



  onSubmit(): void {
    this.normalizeAutocompleteSelections();
    if (this.productForm.invalid) {
      this.productForm.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const formValue = this.productForm.getRawValue();
    const isEdit = this.isEditMode() && this.editingProductId() !== null;

    const request$ = isEdit
      ? this.productService.updateProduct(this.editingProductId() as number, this.buildUpdateRequest(formValue))
      : this.productService.createProduct(this.buildCreateRequest(formValue));

    request$
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response) => {
          if (response.ok && this.embedded()) {
            if (response.body) {
              this.saved.emit(response.body);
            }
            this.toastr.success(isEdit ? 'Producto actualizado correctamente' : 'Producto creado correctamente');
            return;
          }
          if (response.ok) {
            const productId = response.body?.id;
            if (productId) {
              this.isEditMode.set(true);
              this.editingProductId.set(productId);
              this.router.navigate(['/products', productId], { replaceUrl: true });
              this.currentProduct.set(response.body);
              const taxIds = (response.body.taxes || []).map(t => t.id);
              this.productForm.patchValue({ productTaxIds: taxIds }, { emitEvent: false });
              this.syncSelectedTaxesFromForm();
            }
            this.toastr.success(isEdit ? 'Producto actualizado correctamente' : 'Producto creado correctamente');
          } else {
            this.setError(response.message || (isEdit ? 'Error al actualizar el producto' : 'Error al crear el producto'));
          }
        },
        error: (err) => {
          this.setError(err.error?.message || 'Error al conectar con el servidor');
          console.error(isEdit ? 'Error updating product:' : 'Error creating product:', err);
        }
      });
  }

  cancel(): void {
    if (this.embedded()) {
      this.dismissed.emit();
      return;
    }
    this.router.navigate(['/products']);
  }

  /** Recarga el producto desde el API (botón refresh del header). */
  refreshProduct(): void {
    const id = this.editingProductId();
    if (id == null || this.loading()) return;
    this.loadProduct(id);
  }

  readonly statusTooltip = 'Estado del producto: Activo o Inactivo.';

  statusDisplayLabel(): string {
    const state = this.productForm.get('state')?.value;
    return this.stateOptions.find((o) => o.value === state)?.label ?? (state || '—');
  }

  getTaxApplicationTypeLabel(type: string): string {
    if (!type) return '';
    const normalized = String(type).toUpperCase();
    return this.taxApplicationOptions.find(option => option.value === normalized)?.label || type;
  }

  getTaxCollectionTypeLabel(type: string): string {
    if (!type) return '';
    const normalized = String(type).toUpperCase();
    return this.taxCollectionTypeOptions.find(option => option.value === normalized)?.label || type;
  }

  onMeasureUnitSelected(option: MeasureUnitDTO | null): void {
    if (!option) return;
    this.measureUnitSelecting = true;
    const withLabel = this.toMeasureUnitOption(option);
    this.productForm.patchValue({
      measureUnitId: withLabel.id,
      measureUnitSelection: withLabel
    });
  }

  onMeasureUnitBlur(): void {
    this.commitOrClearAutocomplete('measureUnitSelection', 'measureUnitId', 'measureUnitSelecting');
  }

  onMeasureUnitCleared(): void {
    this.clearAutocompleteSelection('measureUnitSelection', 'measureUnitId');
  }

  onTaxCodeSelected(option: TaxProductCodeDTO | null): void {
    if (!option) return;
    this.taxCodeSelecting = true;
    const withLabel = this.toTaxCodeOption(option);
    this.productForm.patchValue({
      taxCodeId: withLabel.id,
      taxCodeSelection: withLabel
    });
  }

  onTaxCodeBlur(): void {
    this.commitOrClearAutocomplete('taxCodeSelection', 'taxCodeId', 'taxCodeSelecting');
  }

  onTaxCodeCleared(): void {
    this.clearAutocompleteSelection('taxCodeSelection', 'taxCodeId');
  }

  displayMeasureUnit(option?: MeasureUnitDTO | null): string {
    if (!option) return '';
    return `${option.code} - ${option.name}`;
  }

  displayTaxProductCode(option?: TaxProductCodeDTO | null): string {
    if (!option) return '';
    return `${option.taxCode} - ${option.taxDescription}`;
  }

  getNameTooltip(): string {
    return String(this.productForm.get('name')?.value || '').trim();
  }

  getSkuTooltip(): string {
    return String(this.productForm.get('sku')?.value || '').trim();
  }

  getExternalIdTooltip(): string {
    return String(this.productForm.get('externalId')?.value || '').trim();
  }

  getUnitPriceTooltip(): string {
    const value = this.productForm.get('unitPrice')?.value;
    if (value === null || value === undefined || value === '') return '';
    return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  getCurrencyTooltip(): string {
    const id = this.productForm.get('currencyId')?.value;
    if (id == null) return '';
    return this.currencySelectOptions.find(c => c.id === id)?.label ?? '';
  }

  getTaxInclusiveTooltip(): string {
    const value = this.productForm.get('taxInclusive')?.value;
    return this.taxInclusiveOptions.find(o => o.value === value)?.label ?? '';
  }

  getMeasureUnitTooltip(): string {
    const value = this.productForm.get('measureUnitSelection')?.value;
    if (!value) return '';
    if (typeof value === 'string') return value;
    return this.displayMeasureUnit(value);
  }

  getTaxCodeTooltip(): string {
    const value = this.productForm.get('taxCodeSelection')?.value;
    if (!value) return '';
    if (typeof value === 'string') return value;
    return this.displayTaxProductCode(value);
  }

  getSatObjetoImpTooltip(): string {
    const id = this.productForm.get('satObjetoImpId')?.value;
    if (id == null) return '';
    return this.satObjetoImpOptions().find(o => o.id === id)?.description ?? '';
  }

  getDescriptionTooltip(): string {
    return String(this.productForm.get('description')?.value || '').trim();
  }

  onMeasureUnitComplete(event: { query: string }): void {
    const query = (event.query || '').trim();
    if (query.length < 3) {
      this.measureUnitOptions.set([]);
      return;
    }
    this.measureUnitLoading.set(true);
    const spec = this.buildMeasureUnitSpec(query);
    this.measureUnitService.listMeasureUnits(spec, { page: 0, size: 20, sort: ['name,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as MeasureUnitDTO[] })),
        finalize(() => this.measureUnitLoading.set(false))
      )
      .subscribe(response => {
        this.measureUnitOptions.set(response.ok ? response.body.map(item => this.toMeasureUnitOption(item)) : []);
      });
  }

  onTaxCodeComplete(event: { query: string }): void {
    const query = (event.query || '').trim();
    if (query.length < 3) {
      this.taxCodeOptions.set([]);
      return;
    }
    this.taxCodeLoading.set(true);
    const spec = this.buildTaxCodeSpec(query);
    this.taxProductCodeService.listTaxProductCodes(spec, { page: 0, size: 20, sort: ['taxCode,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as TaxProductCodeDTO[] })),
        finalize(() => this.taxCodeLoading.set(false))
      )
      .subscribe(response => {
        this.taxCodeOptions.set(response.ok ? response.body.map(item => this.toTaxCodeOption(item)) : []);
      });
  }

  private loadProduct(productId: number): void {
    this.loading.set(true);
    this.formDataLoading.set(true);
    this.clearError();
    this.productService.getProductById(productId).pipe(
      finalize(() => {
        this.loading.set(false);
        this.formDataLoading.set(false);
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.currentProduct.set(response.body);
          const taxes = response.body.taxes || [];
          this.selectedTaxes.set(taxes);
          this.patchProduct(response.body);
        } else {
          this.setError(response.message || 'Error al cargar el producto');
        }
      },
      error: (err) => {
        this.setError('Error al cargar el producto. Verifica que el API esté ejecutándose.');
        console.error('Error loading product for edit:', err);
      }
    });
  }

  private patchProduct(product: ProductDTO): void {
    this.productForm.patchValue({
      name: product.name,
      description: product.description || '',
      sku: product.sku || '',
      externalId: product.externalId || '',
      unitPrice: product.unitPrice ?? 0,
      currencyId: product.currency?.id ?? null,
      measureUnitId: product.measureUnitId || '',
      taxCodeId: product.taxCodeId || '',
      satObjetoImpId: product.satObjetoImpId ?? null,
      taxInclusive: product.taxInclusive ?? null,
      productTaxIds: (product.taxes || []).map(t => t.id),
      state: product.state || 'ACTIVE',
      currencySearch: '',
      measureUnitSearch: '',
      taxCodeSearch: '',
      measureUnitSelection: product.measureUnitId
        ? this.toMeasureUnitOption({ id: product.measureUnitId, code: product.measureUnitCode || '', name: product.measureUnitName || '' })
        : null,
      taxCodeSelection: product.taxCodeId
        ? this.toTaxCodeOption({ id: product.taxCodeId, taxCode: product.taxCodeTaxCode || '', taxDescription: product.taxCodeDescription || '' })
        : null
    });

    const currentMeasure = this.productForm.get('measureUnitSelection')?.value as MeasureUnitDTO | null;
    if (currentMeasure) {
      this.addMeasureUnitOption(currentMeasure);
    }
    const currentTax = this.productForm.get('taxCodeSelection')?.value as TaxProductCodeDTO | null;
    if (currentTax) {
      this.addTaxCodeOption(currentTax);
    }
    const productTaxes = product.taxes || [];
    productTaxes.forEach(tax => this.addProductTaxOption(tax));
    this.syncSelectedTaxesFromForm();
  }

  private buildCreateRequest(formValue: any): CreateProductRequest {
    const measureUnit = formValue.measureUnitSelection as MeasureUnitDTO | null;
    const taxCodeSelection = formValue.taxCodeSelection as TaxProductCodeDTO | null;
    const taxCodeId = taxCodeSelection?.id ?? (formValue.taxCodeId ? Number(formValue.taxCodeId) : undefined);
    return {
      name: formValue.name,
      description: formValue.description || undefined,
      sku: formValue.sku || undefined,
      externalId: formValue.externalId || undefined,
      unitPrice: Number(formValue.unitPrice),
      currencyId: Number(formValue.currencyId),
      measureUnitId: measureUnit?.id ?? (formValue.measureUnitId ? Number(formValue.measureUnitId) : undefined),
      taxCodeId,
      satObjetoImpId: formValue.satObjetoImpId || undefined,
      taxInclusive: formValue.taxInclusive ?? null,
      productTaxIds: formValue.productTaxIds || []
    };
  }

  private buildUpdateRequest(formValue: any): UpdateProductRequest {
    const measureUnit = formValue.measureUnitSelection as MeasureUnitDTO | null;
    const taxCodeSelection = formValue.taxCodeSelection as TaxProductCodeDTO | null;
    const measureUnitId = measureUnit?.id ?? (formValue.measureUnitId ? Number(formValue.measureUnitId) : undefined);
    const taxCodeId = taxCodeSelection?.id ?? (formValue.taxCodeId ? Number(formValue.taxCodeId) : undefined);
    return {
      name: formValue.name || undefined,
      description: formValue.description || undefined,
      sku: formValue.sku || undefined,
      externalId: formValue.externalId || undefined,
      unitPrice: formValue.unitPrice !== '' ? Number(formValue.unitPrice) : undefined,
      currencyId: formValue.currencyId ? Number(formValue.currencyId) : undefined,
      measureUnitId,
      taxCodeId,
      satObjetoImpId: formValue.satObjetoImpId || undefined,
      taxInclusive: formValue.taxInclusive ?? null,
      productTaxIds: formValue.productTaxIds || [],
      state: formValue.state || undefined
    };
  }

  private setupSearchSubscriptions(): void {
    this.productForm.get('currencySearch')?.valueChanges.pipe(
      map(value => typeof value === 'string' ? value.trim() : ''),
      debounceTime(200),
      distinctUntilChanged(),
      tap(query => {
        const filtered = this.filterCurrencies(query);
        this.currencyOptions.set(filtered);
      })
    ).subscribe();

    // Búsqueda de unidad de medida y código fiscal se hace desde completeMethod en el template (PrimeNG AutoComplete).
  }

  private loadAllProductTaxes(page: number = 0, acc: ProductTaxDTO[] = []): void {
    this.productTaxLoading.set(true);
    this.productTaxService.listProductTaxes({ state: 'ACTIVE' }, { page, size: 200, sort: ['id,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as ProductTaxDTO[], page: undefined })),
        finalize(() => this.productTaxLoading.set(false))
      )
      .subscribe(response => {
        if (!response.ok) {
          this.productTaxOptions.set([]);
          return;
        }
        const merged = [...acc, ...response.body];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadAllProductTaxes(response.page.currentPage, merged);
          return;
        }
        this.productTaxOptions.set(merged.map(item => ({ ...item, label: this.productTaxLabel(item) })));
        const productTaxes = this.currentProduct()?.taxes ?? [];
        productTaxes.forEach(tax => this.addProductTaxOption(tax));
        this.syncSelectedTaxesFromForm();
      });
  }

  private addProductTaxOption(option: ProductTaxDTO): void {
    const withLabel = { ...option, label: this.productTaxLabel(option) };
    if (!this.productTaxOptions().some(item => item.id === withLabel.id)) {
      this.productTaxOptions.set([withLabel, ...this.productTaxOptions()]);
    }
  }

  private productTaxLabel(item: ProductTaxDTO): string {
    const name = item.tax?.displayName || item.tax?.taxKey || `Impuesto #${item.id}`;
    const rate = item.taxRate != null ? ` · ${item.taxRate}%` : '';
    const amount = item.taxAmount != null ? ` · $${item.taxAmount}` : '';
    return `${name}${rate}${amount}`;
  }

  private buildMeasureUnitSpec(query: string): Record<string, string> {
    const trimmed = query.trim();
    return { 'name[lk]': trimmed, 'code[lk]': trimmed };
  }

  private buildTaxCodeSpec(query: string): Record<string, string> {
    const trimmed = query.trim();
    return { 'taxDescription[lk]': trimmed, 'taxCode[lk]': trimmed };
  }

  private loadAllSatObjetoImps(page: number = 0, acc: SATObjetoImpDTO[] = []): void {
    this.satObjetoImpService.list({}, { page, size: 200, sort: ['description,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as SATObjetoImpDTO[], page: undefined })),
        finalize(() => {})
      )
      .subscribe(response => {
        if (!response.ok) {
          this.satObjetoImpOptions.set([]);
          return;
        }
        const merged = [...acc, ...response.body];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          this.loadAllSatObjetoImps(response.page.currentPage + 1, merged);
          return;
        }
        this.satObjetoImpOptions.set(merged);
      });
  }

  private loadAllCurrencies(page: number = 0, acc: CurrencyDTO[] = []): void {
    this.currencyLoading.set(true);
    this.currencyService.listCurrencies({}, { page, size: 200, sort: ['code,asc'] })
      .pipe(
        catchError(() => of({ ok: false, body: [] as CurrencyDTO[], page: undefined })),
        finalize(() => this.currencyLoading.set(false))
      )
      .subscribe(response => {
        if (!response.ok) {
          this.allCurrencies.set([]);
          this.currencyOptions.set([]);
          return;
        }

        const merged = [...acc, ...response.body];
        if (response.page && response.page.currentPage < response.page.totalPages) {
          const nextPage = response.page.currentPage;
          this.loadAllCurrencies(nextPage, merged);
          return;
        }

        this.allCurrencies.set(merged);
        this.currencyOptions.set(merged);
        this.applyDefaultCurrencyIfNeeded();
      });
  }

  private getDefaultCurrencyId(): number | null {
    const mxn = this.allCurrencies().find((c) => String(c.code).toUpperCase() === 'MXN');
    return mxn?.id ?? null;
  }

  /** Moneda por defecto en productos nuevos: MXN (Peso mexicano). No aplica al editar. */
  private applyDefaultCurrencyIfNeeded(): void {
    if (this.isEditMode()) return;
    if (this.productForm.get('currencyId')?.value != null) return;
    const mxnId = this.getDefaultCurrencyId();
    if (mxnId == null) return;
    this.productForm.patchValue({ currencyId: mxnId }, { emitEvent: false });
  }

  private filterCurrencies(query: string): CurrencyDTO[] {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return this.allCurrencies();
    return this.allCurrencies().filter(currency => {
      return currency.code.toLowerCase().includes(trimmed) || currency.name.toLowerCase().includes(trimmed);
    });
  }

  private toMeasureUnitOption(option: MeasureUnitDTO): MeasureUnitDTO & { displayLabel: string } {
    return { ...option, displayLabel: this.displayMeasureUnit(option) };
  }

  private addMeasureUnitOption(option: MeasureUnitDTO): void {
    const withLabel = this.toMeasureUnitOption(option);
    const exists = this.measureUnitOptions().some(item => item.id === withLabel.id);
    if (!exists) {
      this.measureUnitOptions.set([withLabel, ...this.measureUnitOptions()]);
    }
  }

  private toTaxCodeOption(option: TaxProductCodeDTO): TaxProductCodeDTO & { displayLabel: string } {
    return { ...option, displayLabel: this.displayTaxProductCode(option) };
  }

  private addTaxCodeOption(option: TaxProductCodeDTO): void {
    const withLabel = this.toTaxCodeOption(option);
    const exists = this.taxCodeOptions().some(item => item.id === withLabel.id);
    if (!exists) {
      this.taxCodeOptions.set([withLabel, ...this.taxCodeOptions()]);
    }
  }

  private isConfirmedAutocompleteValue(value: unknown): boolean {
    return !!value && typeof value === 'object' && (value as { id?: unknown }).id != null;
  }

  private normalizeAutocompleteSelections(): void {
    if (!this.isConfirmedAutocompleteValue(this.productForm.get('measureUnitSelection')?.value)) {
      this.clearAutocompleteSelection('measureUnitSelection', 'measureUnitId');
    }
    if (!this.isConfirmedAutocompleteValue(this.productForm.get('taxCodeSelection')?.value)) {
      this.clearAutocompleteSelection('taxCodeSelection', 'taxCodeId');
    }
  }

  private commitOrClearAutocomplete(
    controlName: 'measureUnitSelection' | 'taxCodeSelection',
    idControlName: 'measureUnitId' | 'taxCodeId',
    selectingFlag: 'measureUnitSelecting' | 'taxCodeSelecting'
  ): void {
    setTimeout(() => {
      if (this[selectingFlag]) {
        this[selectingFlag] = false;
        return;
      }
      const control = this.productForm.get(controlName);
      if (!control) return;
      if (!this.isConfirmedAutocompleteValue(control.value)) {
        this.clearAutocompleteSelection(controlName, idControlName);
      }
    }, 200);
  }

  private clearAutocompleteSelection(
    controlName: 'measureUnitSelection' | 'taxCodeSelection',
    idControlName: 'measureUnitId' | 'taxCodeId'
  ): void {
    this.productForm.patchValue({
      [controlName]: null,
      [idControlName]: ''
    });
    const control = this.productForm.get(controlName);
    control?.markAsTouched();
    control?.markAsDirty();
    control?.updateValueAndValidity();
    this.cdr.markForCheck();
  }

  private updateRelationValidators(isEdit: boolean): void {
    const measureUnit = this.productForm.get('measureUnitSelection');
    const taxCode = this.productForm.get('taxCodeSelection');
    if (!measureUnit || !taxCode) return;
    measureUnit.setValidators([autocompleteOptionIfPresent]);
    taxCode.setValidators([autocompleteOptionIfPresent]);
    measureUnit.updateValueAndValidity();
    taxCode.updateValueAndValidity();
  }

  private clearError(): void {
    this.error.set(null);
  }

  private setError(message: string): void {
    this.error.set(message);
    this.toastr.error(message);
  }
}